// Which matters a TIME-ENTRY matter picker offers: one rule, used by every
// picker where a person chooses the matter for a time entry (the timer form,
// Time Entries "+ Manual Entry", the CSV import's matter lookup).
//
// Firm rules (James, 2026-10-15):
//   1. Only ACTIVE matters, plus the currently selected matter if it is no
//      longer active (the caller labels it "(inactive)") so the select still
//      shows what the entry has.
//   2. A Personal matter belongs to one person: practice_area exactly
//      "Personal" (e.g. "Personal - Vacation - <employee>", whose
//      responsible_attorney is that employee). It is offered ONLY when it
//      belongs to the entry's attorney: the signed-in person, or the attorney
//      an admin is entering time for (on behalf of). Admins do not see other
//      people's personal matters in a time-entry picker. "Personal Injury" is
//      a different practice area and is never treated as personal.
//      Legacy since feedback #842 (2026-10-19): Omnia no longer creates
//      Personal matters (illness and vacation are Calendar entries); the
//      rule stays for one someone creates by hand.
//
// The rows are GET /api/matters?slim=true rows (practice_area and
// responsible_attorney are in its SELECT). POST /api/time-entries enforces the
// same rule (isPersonalPracticeArea in api/src/routes/timeEntries.js: 400
// `personal_matter_owner`, admins included); the bulk route
// POST /api/import/time-entries (api/src/routes/import.js) does not yet.

export const isPersonalMatter = (m) =>
  !!m && String(m.practice_area ?? '').trim().toLowerCase() === 'personal'

// The entry's attorney: the on-behalf pick when there is one, else the
// signed-in person. '' when neither is known.
export const entryAttorneyId = (onBehalfUserId, selfId) => {
  const pick = onBehalfUserId == null ? '' : String(onBehalfUserId).trim()
  if (pick) return pick
  return selfId == null ? '' : String(selfId).trim()
}

// May this matter be chosen for an entry of attorneyId? A personal matter
// needs a known attorney who is its responsible attorney.
export const matterAllowedForAttorney = (m, attorneyId) => {
  if (!isPersonalMatter(m)) return true
  const who = attorneyId == null ? '' : String(attorneyId).trim()
  return who !== '' && m.responsible_attorney != null && String(m.responsible_attorney) === who
}

// → [{ matter, inactive }] in the input order: the matters a time-entry
// picker offers for attorneyId, with selectedId kept when it is inactive.
export function timeEntryMatterChoices (matters, { attorneyId, selectedId } = {}) {
  const sel = selectedId == null ? '' : String(selectedId)
  const out = []
  for (const m of Array.isArray(matters) ? matters : []) {
    if (!m || m.id == null) continue
    if (!matterAllowedForAttorney(m, attorneyId)) continue
    const active = m.status === 'active'
    if (!active && String(m.id) !== sel) continue
    out.push({ matter: m, inactive: !active })
  }
  return out
}

// The selected matter when it is someone else's personal matter (the picker
// must clear it), else null. Nothing is cleared on a guess: no selection, an
// unknown attorney, or a matter the list does not carry → null.
export function foreignPersonalMatter (matters, selectedId, attorneyId) {
  const sel = selectedId == null ? '' : String(selectedId)
  const who = attorneyId == null ? '' : String(attorneyId).trim()
  if (!sel || !who || !Array.isArray(matters)) return null
  const m = matters.find(x => x && String(x.id) === sel)
  if (!m || !isPersonalMatter(m)) return null
  return matterAllowedForAttorney(m, who) ? null : m
}

// What the person sees when a picker clears such a matter.
export const foreignPersonalNote = (m) => {
  const title = String((m && m.title) || '').trim() || 'That matter'
  return `${title} is another person's personal matter, so it was removed — pick a matter for this attorney.`
}
